import React, { useEffect, useState } from 'react';
import { partnerApiService } from '../services/api';
import {
  AdCampaign,
  AdPlacement,
  AD_CAMPAIGN_STATUS_LABELS,
  AD_PLACEMENT_LABELS,
  CreateAdCampaignRequest,
} from '../types/api';
import { formatDate, formatPeriod } from '../utils/period';

// Порядок показа Пн…Вс, значения — формат бэка: 0 (вс) … 6 (сб), как у скидок.
const WEEKDAYS = [
  { value: 1, short: 'Пн' },
  { value: 2, short: 'Вт' },
  { value: 3, short: 'Ср' },
  { value: 4, short: 'Чт' },
  { value: 5, short: 'Пт' },
  { value: 6, short: 'Сб' },
  { value: 0, short: 'Вс' },
];

const PLACEMENTS: AdPlacement[] = ['search', 'home', 'both'];

const TABLE_COLUMNS = 'minmax(180px, 1.4fr) minmax(150px, 1fr) minmax(220px, 1.3fr) 120px 220px';

// pending — жёлтый «на модерации», active — зелёный, paused и archived — серые;
// архив дополнительно приглушает всю строку (qp-tr--muted).
const STATUS_CLASS: Record<AdCampaign['status'], string> = {
  pending: 'qp-status qp-status--warn',
  active: 'qp-status qp-status--ok',
  paused: 'qp-status',
  archived: 'qp-status',
};

/**
 * Подпись и класс бейджа статуса. Тип обещает четыре статуса, но сервер может
 * начать отдавать новый раньше, чем обновится фронт, — незнакомая строка не
 * должна ронять «Промо» TypeError-ом: показываем сырое значение серым бейджем.
 */
function statusBadge(status: AdCampaign['status']): { className: string; label: string } {
  return {
    className: STATUS_CLASS[status] ?? 'qp-status',
    label: AD_CAMPAIGN_STATUS_LABELS[status] ?? String(status ?? '—'),
  };
}

const MODERATION_NOTICE =
  'Кампания на модерации: менеджер активирует её после согласования оплаты';

/** Черновик формы. Пустые строки/массив — «без ограничения», в payload уйдут null. */
interface CampaignDraft {
  placement: AdPlacement;
  starts_at: string;
  ends_at: string;
  weekdays: number[];
  time_start: string;
  time_end: string;
}

const EMPTY_DRAFT: CampaignDraft = {
  placement: 'search',
  starts_at: '',
  ends_at: '',
  weekdays: [],
  time_start: '',
  time_end: '',
};

/** «HH:MM» из формы → «HH:MM:SS» бэка; пусто → null («весь день»). */
const toApiTime = (value: string): string | null => (value ? `${value}:00` : null);

/** Черновик → payload по контракту бэка: null-ы вместо пустот, дни числами по возрастанию. */
function draftToPayload(draft: CampaignDraft): CreateAdCampaignRequest {
  return {
    placement: draft.placement,
    starts_at: draft.starts_at,
    ends_at: draft.ends_at || null,
    weekdays: draft.weekdays.length > 0 ? [...draft.weekdays].sort((a, b) => a - b) : null,
    time_start: toApiTime(draft.time_start),
    time_end: toApiTime(draft.time_end),
  };
}

function campaignToDraft(campaign: AdCampaign): CampaignDraft {
  return {
    placement: campaign.placement,
    starts_at: campaign.starts_at ?? '',
    ends_at: campaign.ends_at ?? '',
    weekdays: campaign.weekdays ?? [],
    time_start: campaign.time_start?.slice(0, 5) ?? '',
    time_end: campaign.time_end?.slice(0, 5) ?? '',
  };
}

/** «с 1 сентября · бессрочно» либо «1 — 30 сентября». */
function periodLabel(campaign: AdCampaign): string {
  if (!campaign.ends_at) {
    return campaign.starts_at ? `с ${formatDate(campaign.starts_at)} · бессрочно` : 'Бессрочно';
  }
  return formatPeriod(campaign);
}

/**
 * Рекламный блок — платные места в поиске и на главной (M14, часть 2в).
 *
 * Партнёр подаёт заявку и правит расписание показов; активация и вес ротации
 * остаются за админом, поэтому созданная кампания всегда «на модерации».
 * Секция живёт на странице «Промо» рядом с историями и баннером.
 */
export const AdCampaignsSection: React.FC = () => {
  const [campaigns, setCampaigns] = useState<AdCampaign[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  /** id кампании, у которой сейчас идёт запрос действия — кнопки строки заперты. */
  const [busyId, setBusyId] = useState<number | null>(null);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editing, setEditing] = useState<AdCampaign | null>(null);
  const [draft, setDraft] = useState<CampaignDraft>(EMPTY_DRAFT);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);

  const loadCampaigns = async () => {
    try {
      setIsLoading(true);
      setLoadError(null);
      setCampaigns(await partnerApiService.getAdCampaigns());
    } catch (error) {
      console.error('Ошибка загрузки рекламных кампаний:', error);
      setLoadError('Не удалось загрузить рекламные кампании');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadCampaigns();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setDraft(EMPTY_DRAFT);
    setFormErrors({});
    setFormError(null);
    setIsFormOpen(true);
  };

  const openEdit = (campaign: AdCampaign) => {
    setEditing(campaign);
    setDraft(campaignToDraft(campaign));
    setFormErrors({});
    setFormError(null);
    setIsFormOpen(true);
  };

  const toggleWeekday = (weekday: number) => {
    setDraft((prev) => ({
      ...prev,
      weekdays: prev.weekdays.includes(weekday)
        ? prev.weekdays.filter((day) => day !== weekday)
        : [...prev.weekdays, weekday],
    }));
  };

  /** Валидация зеркалит бэк: даты по порядку, окно суток — только парой. */
  const validate = (): boolean => {
    const errors: Record<string, string> = {};
    if (!draft.starts_at) errors.starts_at = 'Укажите дату начала показов';
    if (draft.starts_at && draft.ends_at && draft.ends_at < draft.starts_at) {
      errors.ends_at = 'Дата окончания не может быть раньше даты начала';
    }
    // Полуокна не бывает; переход через полночь («с 22:00 до 02:00») — законен.
    if ((draft.time_start === '') !== (draft.time_end === '')) {
      errors.time = 'Время начала и окончания задаются вместе или не задаются вовсе';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const submitForm = async (event: React.FormEvent) => {
    event.preventDefault();
    setFormError(null);
    if (!validate()) return;

    const payload = draftToPayload(draft);
    try {
      setIsSubmitting(true);
      if (editing) {
        await partnerApiService.updateAdCampaign(editing.id, payload);
        setNotice('Кампания обновлена');
      } else {
        await partnerApiService.createAdCampaign(payload);
        setNotice(MODERATION_NOTICE);
      }
      setIsFormOpen(false);
      loadCampaigns();
    } catch (error: any) {
      console.error('Ошибка сохранения кампании:', error);
      setFormError(error.response?.data?.message ?? 'Не удалось сохранить кампанию');
    } finally {
      setIsSubmitting(false);
    }
  };

  /** Общий каркас действий строки: замок busyId, перезагрузка списка, ошибка в notice-строку. */
  const runAction = async (id: number, action: () => Promise<unknown>, failText: string) => {
    try {
      setBusyId(id);
      setNotice(null);
      await action();
      loadCampaigns();
    } catch (error) {
      console.error(failText, error);
      setLoadError(failText);
    } finally {
      setBusyId(null);
    }
  };

  const pause = (campaign: AdCampaign) =>
    runAction(
      campaign.id,
      () => partnerApiService.pauseAdCampaign(campaign.id),
      'Не удалось поставить кампанию на паузу',
    );

  const resume = (campaign: AdCampaign) =>
    runAction(
      campaign.id,
      () => partnerApiService.resumeAdCampaign(campaign.id),
      'Не удалось возобновить кампанию',
    );

  const archive = (campaign: AdCampaign) => {
    if (!window.confirm('Перенести кампанию в архив? Показы прекратятся, вернуть её нельзя.')) return;
    runAction(
      campaign.id,
      () => partnerApiService.archiveAdCampaign(campaign.id),
      'Не удалось перенести кампанию в архив',
    );
  };

  return (
    <section className="qp-panel">
      <header className="qp-panel-head">
        <div>
          <h2 className="qp-label">Рекламный блок</h2>
          <p className="qp-card-sub" style={{ marginTop: 4 }}>
            Платные места: заведение показывается выше остальных в поиске и на главной
          </p>
        </div>
        <button type="button" className="qp-btn qp-btn--primary qp-btn--sm" onClick={openCreate}>
          Новая кампания
        </button>
      </header>

      {notice && (
        <div className="qp-notice qp-notice--accent" role="status">
          {notice}
        </div>
      )}
      {loadError && <div className="qp-notice qp-notice--error">{loadError}</div>}

      {isLoading ? (
        <div className="qp-skel" style={{ height: 180 }} />
      ) : campaigns.length === 0 ? (
        <div className="qp-empty">
          <h3>Рекламных кампаний пока нет</h3>
          <p>
            Кампания поднимает заведение над обычной выдачей — в поиске, на главной или там и там.
            Выберите период и время показов, менеджер активирует кампанию после согласования оплаты.
          </p>
          <button type="button" className="qp-btn qp-btn--primary" onClick={openCreate}>
            Создать кампанию
          </button>
        </div>
      ) : (
        <div className="qp-table">
          <div className="qp-tr qp-thead" style={{ gridTemplateColumns: TABLE_COLUMNS, minWidth: 920 }}>
            <span>Размещение</span>
            <span>Период</span>
            <span>Дни и время</span>
            <span>Статус</span>
            <span className="qp-td-right">Действия</span>
          </div>

          {campaigns.map((campaign) => {
            const isArchived = campaign.status === 'archived';
            const isBusy = busyId === campaign.id;
            const status = statusBadge(campaign.status);
            const allDays = !campaign.weekdays || campaign.weekdays.length === 0;
            const allDay = !campaign.time_start || !campaign.time_end;

            return (
              <div
                key={campaign.id}
                className={`qp-tr${isArchived ? ' qp-tr--muted' : ''}`}
                style={{ gridTemplateColumns: TABLE_COLUMNS, minWidth: 920 }}
              >
                <div style={{ minWidth: 0 }}>
                  <div className="qp-cell-title">{AD_PLACEMENT_LABELS[campaign.placement]}</div>
                  <div className="qp-cell-sub">Заявка №{campaign.id}</div>
                </div>

                <span style={{ fontSize: 12.5, color: 'var(--qp2-muted)' }}>
                  {periodLabel(campaign)}
                </span>

                {allDays && allDay ? (
                  <span style={{ fontSize: 12.5, color: 'var(--qp2-muted)' }}>Все дни · Весь день</span>
                ) : (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    {allDays ? (
                      <span style={{ fontSize: 12.5, color: 'var(--qp2-muted)' }}>Все дни</span>
                    ) : (
                      <span style={{ display: 'flex', gap: 3 }}>
                        {WEEKDAYS.map(({ value, short }) => (
                          <span
                            key={value}
                            className={`qp-daydot${campaign.weekdays!.includes(value) ? ' qp-daydot--on' : ''}`}
                            title={short}
                          >
                            {short}
                          </span>
                        ))}
                      </span>
                    )}
                    <span className="qp-mono" style={{ fontSize: 12.5 }}>
                      {allDay
                        ? 'Весь день'
                        : `${campaign.time_start!.slice(0, 5)}–${campaign.time_end!.slice(0, 5)}`}
                    </span>
                  </span>
                )}

                <span>
                  <span className={status.className}>{status.label.toUpperCase()}</span>
                </span>

                <span
                  className="qp-row-actions"
                  style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap' }}
                >
                  {campaign.status === 'active' && (
                    <button
                      type="button"
                      className="qp-btn qp-btn--outline qp-btn--sm"
                      disabled={isBusy}
                      onClick={() => pause(campaign)}
                    >
                      Пауза
                    </button>
                  )}
                  {campaign.status === 'paused' && (
                    <button
                      type="button"
                      className="qp-btn qp-btn--outline qp-btn--sm"
                      disabled={isBusy}
                      onClick={() => resume(campaign)}
                    >
                      Возобновить
                    </button>
                  )}
                  {!isArchived && (
                    <>
                      <button
                        type="button"
                        className="qp-btn qp-btn--outline qp-btn--sm"
                        disabled={isBusy}
                        onClick={() => openEdit(campaign)}
                      >
                        Редактировать
                      </button>
                      <button
                        type="button"
                        className="qp-btn qp-btn--danger qp-btn--sm"
                        disabled={isBusy}
                        onClick={() => archive(campaign)}
                      >
                        В архив
                      </button>
                    </>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {isFormOpen && (
        <div className="qp-lightbox" role="dialog" aria-modal="true" onClick={() => setIsFormOpen(false)}>
          <form className="qp-modal" onClick={(event) => event.stopPropagation()} onSubmit={submitForm}>
            <header
              style={{
                padding: '14px 18px',
                borderBottom: '1px solid var(--qp2-line)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>
                {editing ? 'Изменить кампанию' : 'Новая кампания'}
              </h3>
              <button type="button" className="qp-link" onClick={() => setIsFormOpen(false)}>
                Закрыть
              </button>
            </header>

            <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 16 }}>
              {formError && <div className="qp-notice qp-notice--error">{formError}</div>}

              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <span className="qp-label">Где показываться</span>
                <div className="qp-chips" role="group" aria-label="Где показываться">
                  {PLACEMENTS.map((placement) => (
                    <button
                      key={placement}
                      type="button"
                      className="qp-chip"
                      aria-pressed={draft.placement === placement}
                      disabled={isSubmitting}
                      onClick={() => setDraft((prev) => ({ ...prev, placement }))}
                    >
                      {AD_PLACEMENT_LABELS[placement]}
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <label className="qp-label" htmlFor="ad-starts-at">
                    Начало показов
                  </label>
                  <input
                    id="ad-starts-at"
                    type="date"
                    className="qp-control qp-control--num"
                    value={draft.starts_at}
                    disabled={isSubmitting}
                    onChange={(event) => {
                      setDraft((prev) => ({ ...prev, starts_at: event.target.value }));
                    }}
                  />
                  {formErrors.starts_at && (
                    <span className="qp-field-error" role="alert">
                      {formErrors.starts_at}
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <label className="qp-label" htmlFor="ad-ends-at">
                    Окончание
                  </label>
                  <input
                    id="ad-ends-at"
                    type="date"
                    className="qp-control qp-control--num"
                    value={draft.ends_at}
                    min={draft.starts_at || undefined}
                    disabled={isSubmitting}
                    onChange={(event) => {
                      setDraft((prev) => ({ ...prev, ends_at: event.target.value }));
                    }}
                  />
                  {formErrors.ends_at ? (
                    <span className="qp-field-error" role="alert">
                      {formErrors.ends_at}
                    </span>
                  ) : (
                    <span className="qp-card-sub">Включительно. Пусто — бессрочно.</span>
                  )}
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <span className="qp-label">Дни недели</span>
                <div className="qp-chips" role="group" aria-label="Дни недели">
                  {WEEKDAYS.map((day) => (
                    <button
                      key={day.value}
                      type="button"
                      className="qp-chip qp-chip--day"
                      aria-pressed={draft.weekdays.includes(day.value)}
                      disabled={isSubmitting}
                      onClick={() => toggleWeekday(day.value)}
                    >
                      {day.short}
                    </button>
                  ))}
                </div>
                <span className="qp-card-sub">Ничего не выбрано — показ во все дни.</span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <label className="qp-label" htmlFor="ad-time-start">
                    Время с
                  </label>
                  <input
                    id="ad-time-start"
                    type="time"
                    className="qp-control qp-control--num"
                    value={draft.time_start}
                    disabled={isSubmitting}
                    onChange={(event) => {
                      setDraft((prev) => ({ ...prev, time_start: event.target.value }));
                    }}
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <label className="qp-label" htmlFor="ad-time-end">
                    Время до
                  </label>
                  <input
                    id="ad-time-end"
                    type="time"
                    className="qp-control qp-control--num"
                    value={draft.time_end}
                    disabled={isSubmitting}
                    onChange={(event) => {
                      setDraft((prev) => ({ ...prev, time_end: event.target.value }));
                    }}
                  />
                </div>
              </div>
              {formErrors.time ? (
                <span className="qp-field-error" role="alert" style={{ marginTop: -10 }}>
                  {formErrors.time}
                </span>
              ) : (
                <span className="qp-card-sub" style={{ marginTop: -10 }}>
                  Пусто — весь день. Окно может переходить через полночь: «с 22:00 до 02:00».
                </span>
              )}

              <div style={{ display: 'flex', gap: 8 }}>
                <button type="submit" className="qp-btn qp-btn--primary" disabled={isSubmitting}>
                  {isSubmitting
                    ? 'Сохраняем…'
                    : editing
                      ? 'Сохранить'
                      : 'Отправить на модерацию'}
                </button>
                <button
                  type="button"
                  className="qp-btn qp-btn--ghost"
                  disabled={isSubmitting}
                  onClick={() => setIsFormOpen(false)}
                >
                  Отмена
                </button>
              </div>
            </div>
          </form>
        </div>
      )}
    </section>
  );
};
